import useCurrentlyPlayingSong from "@hooks/useCurrentlyPlayingSong";
import useUserPlaylists from "@hooks/usePlayList";
import FavoriteIcon from "@mui/icons-material/Favorite";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Typography,
  useTheme
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

interface PlaylistModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PlaylistModal: React.FC<PlaylistModalProps> = ({ isOpen, onClose }) => {
  const [playlists, setPlaylists] = useState<any[]>([]);
  const { currentlyPlayingObject } = useCurrentlyPlayingSong();
  const trackId = currentlyPlayingObject?.item?.uri;
  const trackName = currentlyPlayingObject?.item?.name;
  const { toggleLikedSongs, fetchPlaylists, fetchLikedSongs, toggleSongToPlaylist } =
    useUserPlaylists();

  const theme = useTheme();

  const handleSelectPlaylist = async (playlistId: string, playListName: string) => {
    if (!trackId) {
      return;
    }
    try {
      let added = false;
      if (playlistId === "liked-songs") {
        added = await toggleLikedSongs(trackId);
      } else {
        added = await toggleSongToPlaylist(playlistId, trackId);
      }

      toast.success(
        added
          ? `Added ${trackName} to playlist ${playListName}`
          : `Song ${trackName} already present at ${playListName}`
      );
      onClose();
    } catch (err) {
      toast.error("Error adding song to playlist");
    }
  };

  useEffect(() => {
    if (isOpen) {
      const loadPlaylists = async () => {
        const fetchedPlaylists = await fetchPlaylists();
        const likedSongsPlaylist = await fetchLikedSongs();

        // Combine playlists and liked songs, remove duplicates
        const allPlaylists = [likedSongsPlaylist, ...fetchedPlaylists];
        const uniquePlaylists = Array.from(new Set(allPlaylists.map(p => p.id))).map(id =>
          allPlaylists.find(p => p.id === id)
        );

        setPlaylists(uniquePlaylists);
      };
      loadPlaylists();
    }
  }, [isOpen, fetchPlaylists]);

  const handlePlaylistClick = (playlistId: string, playListName: string) => {
    // Logic to check if the playlist contains the track and call onSelectPlaylist
    handleSelectPlaylist(playlistId, playListName);
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="add-to-playlist-modal"
      aria-describedby="add-song-to-playlist"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: theme.palette.background.paper,
          border: "2px solid #000",
          boxShadow: 24,
          p: 4,
          color: theme.palette.text.primary
        }}
      >
        <h2 id="add-to-playlist-modal">Add to Playlist</h2>
        <List>
          {playlists.map(playlist => (
            <ListItem
              button
              key={playlist.id}
              onClick={() => handlePlaylistClick(playlist.id, playlist.name)}
            >
              <ListItemIcon>
                {playlist.id === "liked-songs" ? (
                  <FavoriteIcon style={{ width: 40, height: 40 }} />
                ) : (
                  <img src={playlist.icon} alt={playlist.name} style={{ width: 40, height: 40 }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={playlist.name}
                secondary={
                  <Typography variant="body2" color="textSecondary">
                    {playlist.total} songs
                  </Typography>
                }
              />
              <ListItemIcon>
                <PlaylistPlayIcon />
              </ListItemIcon>
            </ListItem>
          ))}
        </List>
        <Button variant="outlined" color="secondary" onClick={onClose}>
          Cancel
        </Button>
      </Box>
    </Modal>
  );
};

export default PlaylistModal;
